import PencilIcon from '@heroicons/react/outline/PencilIcon';
import { useMultiStepApplication } from '../../../../store/Context/MultiStepApplicationsProvider';
import { SectionTitle } from '../../../UI/JsonForm/SectionTitle';
import styles from '../../Applications.module.css';
import {
  JsonInput,
  JsonInputType,
} from '../../../UI/JsonForm/JsonInputRenderer/types';
import { formatDate3 } from '../../../../utils';
import Badge from '../../../UI/Badge';
import { PDFDownloadLink, Text, View } from '@react-pdf/renderer';
import { PdfDownload } from '../PdfDownload';

export const ApplicationPreview = () => {
  const { formJSON, setCurrentStep, data } = useMultiStepApplication();

  const renderLabel = (
    type: JsonInputType,
    label: string | number | undefined
  ) => {
    const classNames = 'text-sm font-medium text-gray-500';
    switch (type) {
      case 'info':
        return (
          <dt
            className={`${classNames} col-span-3`}
            dangerouslySetInnerHTML={{ __html: `${label}` }}
          />
        );
      default:
        return <dt className={classNames}>{label}</dt>;
    }
  };
  const renderValue = (type: JsonInputType, value: any) => {
    switch (type) {
      case 'checkbox':
        return value ? 'Yes' : 'No';
      case 'checkbox-group':
        return value.join(', ');
      case 'date':
        return value ? formatDate3(value) : 'N/A';
      case 'file':
        return value?.length
          ? value.map((file: any) => (
              <Badge status='primary' text={file.name} />
            ))
          : 'N/A';
      case 'input':
        return value;
      case 'masked':
        return value;
      case 'radio':
        return value;
      case 'radio-group':
        return value;
      case 'select':
        return value;
      case 'textarea':
        return value;
      case 'info-with-inputs':
        return 'info'; // ! should be "info" check is implemented to not render this value
      case 'info':
        return 'info'; // ! should be "info" check is implemented to not render this value
      default:
        return '';
    }
  };

  const renderArray = ({
    inputs,
    arrayName,
    stepIndex,
  }: {
    inputs: JsonInput[];
    arrayName: string;
    stepIndex: number;
  }) => {
    const arrayNames = arrayName.split('.') || [];
    const arrayValues = arrayNames.reduce<any>((acc, currentName) => {
      acc = (acc || data)[currentName as any];
      return acc;
    }, undefined);

    return (
      <>
        {arrayValues?.map((eachValue: any, index: number) => {
          return renderInputValues({
            inputs,
            passedArrayName: arrayName,
            values: eachValue,
            arrayIndex: index,
            stepIndex,
          });
        })}
      </>
    );
  };

  const renderInputValues = ({
    inputs,
    values,
    arrayIndex,
    stepIndex,
    passedArrayName,
  }: {
    inputs: JsonInput[];
    values?: any;
    arrayIndex?: number;
    passedArrayName?: string;
    stepIndex: number;
  }) => {
    return (
      <>
        {inputs.map((input, index) => {
          const isEven = index % 2 === 0;
          const backgroundColor = isEven ? 'bg-white' : 'bg-gray-100';
          const {
            inputs: nestedInputs,
            arrayName,
            isArray,
            type,
            props: { name, label },
          } = input;

          const names = name?.split('.') || [];
          const actualData = values || data;
          let value = undefined;
          if (type === 'file') {
            const selectedFiles =
              data.selectedFiles && data.selectedFiles[stepIndex];
            if (selectedFiles) {
              if (arrayIndex !== undefined) {
                const arrayFieldNameForFile = `${passedArrayName}.${arrayIndex}.${name}:${actualData.arrayId}`;
                value = selectedFiles[arrayFieldNameForFile] || [];
              } else {
                value = selectedFiles[name || ''] || [];
              }
            } else {
              value = [];
            }
          } else {
            value = names.reduce<any>((acc, currentName) => {
              acc = (acc || actualData)[currentName];
              return acc;
            }, undefined);
          }

          if (isArray && nestedInputs && arrayName) {
            return renderArray({
              inputs: nestedInputs,
              arrayName,
              stepIndex: stepIndex || 0,
            });
          }

          if (!renderValue(type, value)) {
            return <></>;
          }

          return (
            <>
              <div
                key={name}
                className={`${backgroundColor} px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}
              >
                {arrayIndex !== undefined && index === 0 && (
                  <>
                    <div
                      className={`sm:col-span-3 sm:mt-0 mt-1 border-b-2 pb-4 flex justify-center`}
                    >
                      <p
                        className='bg-primaryColor-normal font-bold text-white p-2 rounded-full flex justify-center'
                        style={{ width: '38px', height: '38px' }}
                      >
                        {arrayIndex + 1}
                      </p>
                    </div>
                  </>
                )}
                {renderValue(type, value) && renderLabel(type, label)}
                {type !== 'info' && (
                  <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                    {renderValue(type, value) !== 'info' &&
                      renderValue(type, value)}
                    {nestedInputs &&
                      !isArray &&
                      renderInputValues({ inputs: nestedInputs, stepIndex })}
                  </dd>
                )}
              </div>
            </>
          );
        })}
      </>
    );
  };

  return (
    <div className={`space-y-5 px-6 py-4 lg:col-span-12`}>
      {/* <div className='w-full flex flex-row-reverse'>
        <PDFDownloadLink
          fileName='Chicago Neighborhood Rebuild Program Application'
          document={<PdfDownload formJSON={formJSON} data={data} />}
        >
          {({ loading, error }) => {
            return (
              <View
                style={{
                  backgroundColor: loading ? '#d3d3d4' : '#10b981',
                  padding: 8,
                  borderRadius: 4,
                }}
              >
                <Text style={{ color: 'white' }}>
                  {loading ? 'Loading...' : 'Download application'}
                </Text>
              </View>
            );
          }}
        </PDFDownloadLink>
      </div> */}
      {formJSON.map((json, index) => {
        const { sections, title, isReview } = json;
        if (isReview) {
          return undefined;
        }
        return (
          <div className={styles['card']} key={`step-${index}`}>
            <div className={styles['card-head-section']}>
              <h3 className={styles['card-title']}>{title}</h3>
              {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and application.</p> */}
              <div
                className='bg-gray-100 p-2 rounded-full hover:bg-blue-200 cursor-pointer'
                onClick={() => setCurrentStep(index)}
              >
                <PencilIcon className='h-5 w-5 text-blue-500' />
              </div>
            </div>
            {sections.map((section) => {
              const { id, title, inputs } = section;
              return (
                <div key={id} className={styles['card-body-section']}>
                  <div className='mb-4'>
                    {title && <SectionTitle title={title} />}
                  </div>
                  <dl>{renderInputValues({ inputs, stepIndex: index })}</dl>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
